import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthCallback } from "@/components/GoogleAuth";
import { Toaster } from "sonner";
import "@/App.css";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { UnitsProvider } from "@/context/UnitsContext";
import { AuthProvider } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import Overview from "@/pages/Overview";
import PressureControl from "@/pages/PressureControl";
import Hydrostatic from "@/pages/Hydrostatic";
import Capacities from "@/pages/Capacities";
import TubingMovement from "@/pages/TubingMovement";
import FreePoint from "@/pages/FreePoint";
import CollapseBurst from "@/pages/CollapseBurst";
import MudWeight from "@/pages/MudWeight";
import KillSheet from "@/pages/KillSheet";
import Tally from "@/pages/Tally";
import SavedJobs from "@/pages/SavedJobs";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import SpecLibrary from "@/pages/SpecLibrary";
import TeamPage from "@/pages/Team";
import Referrals from "@/pages/Referrals";
import DirectionalDrilling from "@/pages/DirectionalDrilling";
import KickTolerance from "@/pages/KickTolerance";
import CasingWizard from "@/pages/CasingWizard";
import Fishing from "@/pages/Fishing";
import Cementing from "@/pages/Cementing";
import TorqueDrag from "@/pages/TorqueDrag";
import PwaInstall from "@/components/PwaInstall";
import SharedResult from "@/pages/SharedResult";
import HardHat from "@/pages/HardHat";
import ToolsIndex from "@/pages/ToolsIndex";
import ToolPage from "@/pages/ToolPage";

// Wrap any calculator/reference page with ProtectedRoute
const P = (Component) => (
  <ProtectedRoute>
    <Component />
  </ProtectedRoute>
);

function AppRoutes() {
  const location = useLocation();
  // Returning from Google sign-in: exchange session_id before any route renders.
  if (location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/r/:id" element={<SharedResult />} />
      <Route path="/hardhat" element={<HardHat />} />
      <Route element={<Layout />}>
        {/* Public marketing / auth-flow pages */}
        <Route index element={<Overview />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/tools" element={<ToolsIndex />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/referrals" element={<Referrals />} />

        {/* All calculators + reference pages require a paid plan */}
        <Route path="/pressure-control" element={P(PressureControl)} />
        <Route path="/kill-sheet" element={P(KillSheet)} />
        <Route path="/tally" element={P(Tally)} />
        <Route path="/kick-tolerance" element={P(KickTolerance)} />
        <Route path="/tubing-movement" element={P(TubingMovement)} />
        <Route path="/directional" element={P(DirectionalDrilling)} />
        <Route path="/torque-drag" element={P(TorqueDrag)} />
        <Route path="/free-point" element={P(FreePoint)} />
        <Route path="/fishing" element={P(Fishing)} />
        <Route path="/collapse-burst" element={P(CollapseBurst)} />
        <Route path="/hydrostatic" element={P(Hydrostatic)} />
        <Route path="/capacities" element={P(Capacities)} />
        <Route path="/mud-weight" element={P(MudWeight)} />
        <Route path="/cementing" element={P(Cementing)} />
        <Route path="/casing-wizard" element={P(CasingWizard)} />
        <Route path="/specs" element={P(SpecLibrary)} />
        <Route path="/saved" element={P(SavedJobs)} />
        <Route path="/team" element={P(TeamPage)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <UnitsProvider>
        <BrowserRouter>
          <AppRoutes />
          <AuthModal />
          <PwaInstall />
        </BrowserRouter>
        <Toaster
          theme="dark"
          toastOptions={{
            style: {
              background: "#11151c",
              border: "1px solid #334155",
              color: "#f8fafc",
              borderRadius: 0,
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 12,
            },
          }}
        />
      </UnitsProvider>
    </AuthProvider>
  );
}

export default App;
